import { Component, Input, OnInit } from '@angular/core';
import { FacturationService } from 'apps/idl/src/modules/facturation/facturation.service';
import { concatMap, mergeMap, skip, take, takeUntil, tap } from 'rxjs/operators';
import { Invoice } from 'apps/idl/src/modules/facturation/models/invoice';
import { Observable, from } from 'rxjs';
import { EInvoiceStatus } from 'apps/idl/src/modules/facturation/models/einvoice-status';
import { IPatient } from 'apps/idl/src/modules/patients/model/IPatient';
import { PatientsService } from 'apps/idl/src/modules/patients/services/patients.service';
import { DrawerPopoverService } from '../../../features/shared/services/drawer-popover.service';
import { FiltreInfirmiersComponent } from '../../../features/facturation/components/filtres/filtre-infirmiers/filtre-infirmiers.component';
import { ContactsService } from 'apps/idl/src/services/security';
import { EPrefix, IContact } from 'apps/idl/src/model';
import { ArrayHelper, UserHelper } from 'apps/idl/src/helpers';
import { DeviceService } from '../../../features/shared/services/device.service';
import { DestroyableComponentBase } from 'apps/idl/src/modules/utils/components/destroyable-component-base';
import { FacturationSVService } from '../../../features/shared/services/facturation-sv.service';
import { PanneauService } from '../../../features/shared/services/panneau.service';
import { FacturationSvComponent } from '../../../features/facturation/components/facturation-sv/facturation-sv.component';
import { EModeFacturation } from '../../../features/shared/enums/EModeFacturation';
import { EModeSecurisation } from 'apps/idl/src/modules/facturation/models/emode-securisation.enum';

@Component({
	selector: 'di-factures-preparees',
	templateUrl: './factures-preparees.component.html',
	styleUrls: ['./factures-preparees.component.scss'],
})
export class FacturesPrepareesComponent extends DestroyableComponentBase implements OnInit {
	@Input() public openPanneauFactureMois: Function;

	public isMobile: boolean = false;
	public invoices: Invoice[];
	public filteredInvoices: Invoice[];
	public facturesCount: number;
	public facturesPriceTotal: number;
	public searchedValue: string = "";
	public patientsMap: Map<string, IPatient> = new Map();
	public filterInfirmiersSelected: string[] = [];
	public colorFilter: string = "Indigo";
	public infirmiers: IContact[];
	public idsInfirmiersSelected: string[] = [];
	public preparedInvoicesChecked: Invoice[] = [];
	public invoicesCheckbox: boolean = false;
	public organizedLetters: string[] = [];
	public organizedInvoices: { [key: string]: Invoice[] } = {};

	public sousTexteAucuneFacture: string = "Vous retrouverez ici toutes les factures préparées dans l'écran de facturation";
	public texteAucuneFacture: string = "Aucune facture préparée";
	public readonly libelleFacSansCV: string = "Facturation sans carte Vitale";
	public libelleBtnFacSansCV: string = this.libelleFacSansCV;
	public messageAlerteFacSansCV: string = "Vous pouvez sélectionner plusieurs factures préparées. Elles seront facturées en dégradé (sans carte Vitale), les unes après les autres.";

	public errorInvoice: boolean = false;
	public successInvoice: boolean = false;
	public messageAlerteSuccesFac: string = "";
	public messageAlerteErreurFac: string = "";

	constructor(
		private svcFacturation: FacturationService,
		private svcPatients: PatientsService,
		private svcContacts: ContactsService,
		private svcDrawerPopover: DrawerPopoverService,
		private svcFacturationSV: FacturationSVService,
		private svcDevice: DeviceService,
		private svcPanneau: PanneauService,
	) {
		super();
	}

	ngOnInit() {
		this.svcDevice.isMobile$.pipe(
			takeUntil(this.destroyed$)
		).subscribe((flag: boolean) => {
			this.isMobile = flag;
		});

		this.svcFacturation.invoiceChanges$.pipe(
			skip(1),
			takeUntil(this.destroyed$),
			mergeMap(() => this.getFacturesPreparees()),
			tap(() => {
				this.assignInfirmiersAndPatientToInvoice();
				this.applyFilter();
			})

		).subscribe();

		this.getFacturesPreparees().pipe(
			mergeMap(() => this.setPatientsMap()),
			mergeMap(() => this.getInfirmiers()),
			tap(() => {
				this.assignInfirmiersAndPatientToInvoice()
				this.applyFilter();
			})
		).subscribe();
	}

	private getFacturesPreparees(): Observable<Invoice[]> {
		return this.svcFacturation.getInvoiceByStatus(EInvoiceStatus.priced).pipe(
			tap((invoices: Invoice[]) => {
				this.invoices = invoices;
				this.filteredInvoices = invoices;
				this.setInvoicesCount();
			}),
		)
	}

	private assignInfirmiersAndPatientToInvoice() {
		// Assignation des patients et infirmiers dans la facture
		this.invoices.forEach(invoice => {
			invoice.infirmier = this.infirmiers.find(x => x._id === invoice.intervenantId);
			invoice.patient = this.patientsMap.get(invoice.patientId);
		});
	}

	private organizeInvoicesByPatient() {
		this.organizedInvoices = this.filteredInvoices.reduce((acc, invoice) => {
			const firstLetter = invoice.patient?.lastName?.trimStart().charAt(0).toUpperCase();
			if (!acc[firstLetter]) {
				acc[firstLetter] = [];
			}
			acc[firstLetter].push(invoice);
			return acc;
		}, {} as { [key: string]: Invoice[] });

		this.organizedLetters = Object.keys(this.organizedInvoices).sort();
	}

	private setInvoicesCount(): void {
		this.facturesCount = this.filteredInvoices.length;
		this.facturesPriceTotal = this.filteredInvoices.reduce((total, invoice) => {
			const invoiceTotal = invoice.actes?.reduce((subTotal, line) => {
				return subTotal + (line.partAMO ?? 0) + (line.partAMC ?? 0) + (line.partPatient ?? 0);
			}, 0) ?? 0;

			return total + invoiceTotal;
		}, 0);
	}

	private setPatientsMap(): Observable<IPatient[]> {
		let patientIdsSet: Set<string> = new Set();

		this.invoices.forEach(inv => {
			if (inv.patientId) {
				patientIdsSet.add(inv.patientId);
			}
		})

		let patientIds = Array.from(patientIdsSet).filter(id => id);
		return this.svcPatients.getPatientsByIds(patientIds).pipe(
			tap((patients: IPatient[]) => {
				this.patientsMap = new Map(patients.map(patient => [patient._id, patient]));
			})
		)
	}

	public facturerSansCV() {
		this.invoicesCheckbox = !this.invoicesCheckbox;
		this.libelleBtnFacSansCV = !this.invoicesCheckbox ? this.libelleFacSansCV : "Annuler";
		if (this.invoicesCheckbox) {
			//Lorsque l'on affiche les checkbox on vide les factures sélectionnées
			this.preparedInvoicesChecked = [];
		};
	}

	public searchInvoicesByTerm(searchValue: string) {
		this.searchedValue = searchValue.trim().toLowerCase();
		this.applyFilter();
	}

	private applyFilter() {
		this.filteredInvoices = this.invoices;
		this.filterInvoicesByInfirmier();
		if (this.searchedValue?.length > 0) {
			this.filterInvoicesByTerm();
		}

		this.preparedInvoicesChecked = this.preparedInvoicesChecked.filter(invoice =>
			this.filteredInvoices.some(filtered => filtered._id === invoice._id)
		);
		this.setInvoicesCount();
		this.organizeInvoicesByPatient();
	}

	private filterInvoicesByTerm() {
		const searchTerms = this.searchedValue.split(/\s+/).filter(term => term);
		this.filteredInvoices = this.filteredInvoices.filter(invoice => {
			const patient = this.patientsMap.get(invoice.patientId);
			if (!patient) return false;

			const firstName = patient.firstName ? patient.firstName.toLowerCase() : '';
			const lastName = patient.lastName ? patient.lastName.toLowerCase() : '';
			const usualLastName = patient.usualLastName ? patient.usualLastName.toLowerCase() : '';

			const fullName = `${firstName} ${usualLastName} ${lastName}`.trim();

			return searchTerms.every(term => fullName.includes(term));
		});
	}

	private filterInvoicesByInfirmier() {
		this.filteredInvoices = this.filteredInvoices.filter((invoice: Invoice) => this.idsInfirmiersSelected.includes(invoice.authorId));
	}

	private getInfirmiers(): Observable<IContact[]> {
		return this.svcContacts.getSiteContactsAnakin([], EPrefix.contact, true, true).pipe(
			tap((contacts: IContact[]) => {
				this.infirmiers = contacts.filter(contact => contact.userId);
				this.idsInfirmiersSelected.push(this.infirmiers.find((contact: IContact) => contact._id === UserHelper.getUserContactId())?._id);
				this.getSelectedInfirmiers();
			}),
		)
	}

	public selectionNewInfirmiers = (idsInfirmiers: string[]): void => {
		this.idsInfirmiersSelected = idsInfirmiers;
		this.getSelectedInfirmiers();
		this.svcDrawerPopover.close();
		this.applyFilter();
	}

	private getSelectedInfirmiers() {
		const newFilterInfirmierSelected = this.infirmiers
			.filter(x => this.idsInfirmiersSelected.includes(x._id))
			.map(x => {
				if (this.idsInfirmiersSelected.length === 1) {
					this.colorFilter = x.avatarCouleur ?? "Indigo";
				}
				const firstLetter = x.firstName?.trimStart().charAt(0).toUpperCase() || "";
				return `${firstLetter}${x.firstName?.slice(1) || ""} ${x.lastName.toUpperCase()}`;
			});
		ArrayHelper.resetArray(this.filterInfirmiersSelected, newFilterInfirmierSelected);
	}

	public openMenuFiltreInfirmier = (event: Event) => {
		this.svcDrawerPopover.open(
			"",
			this.isMobile ? "auto" : "",
			event.currentTarget,
			FiltreInfirmiersComponent,
			{
				infirmiers: this.infirmiers,
				idsInfirmiersSelected: this.idsInfirmiersSelected,
				onFilter: this.selectionNewInfirmiers,
				nonAffectes: false,
				multiple: false
			}
		);
	}

	public updateCheckedInvoices = (invoiceChecked: Invoice, checked: boolean): void => {
		if (checked) {
			this.preparedInvoicesChecked.push(invoiceChecked);
		} else {
			this.preparedInvoicesChecked = this.preparedInvoicesChecked.filter(invoice =>
				invoice._id !== invoiceChecked._id
			);
		}
	}

	public checkAllInvoices = (check: boolean) => {
		if (check) {
			this.preparedInvoicesChecked = this.filteredInvoices;
		} else {
			this.preparedInvoicesChecked = [];
		}
	}

	public isInvoiceChecked(item: Invoice): boolean {
		return this.preparedInvoicesChecked.some(invoice => invoice._id === item._id);
	}

	public facturerDegradeEnMasse = async () => {
		const invoiceToBill: Invoice[] = this.preparedInvoicesChecked;
		const nbTotalInvoiceToBill: number = invoiceToBill?.length ?? 0;

		//Aucune invoice à facturer
		if (!nbTotalInvoiceToBill) return;

		this.initErrorSuccessCounter();

		//On passe toutes les factures en mode dégradée et on save pour ne le faire qu'une fois
		invoiceToBill.forEach(invoice => {
			invoice.securisationMode = EModeSecurisation.DEGRADE;
		});
		await this.svcFacturation.saveInvoices(invoiceToBill).toPromise();

		let results: { invoice: Invoice, success: boolean }[] = [];

		from(this.preparedInvoicesChecked).pipe(
			concatMap((invoice, index) => {
				this.svcPanneau.open(`Facturation dégradée ${index + 1}/${nbTotalInvoiceToBill}`, FacturationSvComponent, {
					invoice,
					patient: invoice.patient,
					mode: EModeFacturation.DEGRADE
				});

				return this.svcFacturationSV.invoiceProcessed$.pipe(
					take(1),
					tap(({ success, invoice }) => {
						results.push({ invoice, success });
					}),
					tap(() => {
						//On ferme le panneau précédent
						this.svcPanneau.close();
					})
				);
			})
		).subscribe({
			complete: () => {
				const successCount = results.filter(r => r.success).length;
				const errorCount = results.filter(r => !r.success).length;
				//TODO :  a décommenter si on appel saveInvoices dans facturation-sv.component
				// this.svcFacturation.invoiceChange();

				//On masque le bandeau + on remet le bouton à l'initial
				this.facturerSansCV();

				if (errorCount) {
					//On affiche le bandeau rouge avec le nombre d'erreurs
					this.errorInvoice = true;
					this.messageAlerteErreurFac = `${errorCount} facture${errorCount > 1 ? 's ont échoué' : ' a échoué'} lors de la facturation. Veuillez vérifier les erreurs signalées pour les corriger.`
				}
				if (successCount) {
					//On affiche le bandeau vert avec le nombre de succès
					this.successInvoice = true;
					this.messageAlerteSuccesFac = `${successCount} facture${successCount > 1 ? 's' : ''} en mode dégradé ${successCount > 1 ? 'ont été générées' : 'a été générée'}. Vous pouvez les télétransmettre.`
				}

				//On décoche toutes les invoices sélectionnées
				this.preparedInvoicesChecked = [];

				//On met a jour la pastille du menu	
				this.svcFacturationSV.updateFacturesPrepareesBadgeCount().subscribe();
			}
		});
	}

	private initErrorSuccessCounter() {
		this.successInvoice = false;
		this.errorInvoice = false;
		this.messageAlerteErreurFac = "";
		this.messageAlerteSuccesFac = "";
	}
}